/* Profile (prototype .pfHead, .pfSec, .pfRow, .pfField, .pfCard, .brand, .pfSet, .pfFoot), in the telify layer. */
@layer telify {
  /* One centered 720px column (Gabriel 2026-10-19: "the profile page is a bit too wide"). */
  .body {
    max-width: 720px;
    margin: 0 auto;
    padding-bottom: 96px;
  }

  .head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 32px;
    margin-top: 52px;
    padding-bottom: 30px;
    border-bottom: var(--line-strong);
  }

  .who {
    display: flex;
    align-items: center;
    gap: 26px;
    min-width: 0;
  }

  .av {
    width: 84px;
    height: 84px;
    flex-shrink: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    object-fit: cover;
    background: var(--avatar-bg);
    color: var(--avatar-initials-color);
    font-size: 27px;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  .h1 {
    margin-top: 10px;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 56px;
    letter-spacing: -0.015em;
    line-height: 1.05;
  }

  .meta {
    margin-top: 10px;
    font-size: 16px;
    color: var(--text-secondary);
  }

  .sess {
    text-align: right;
    padding-bottom: 6px;
    flex-shrink: 0;
  }

  .sess b {
    display: block;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 46px;
    line-height: 1;
    color: var(--accent);
    font-variant-numeric: tabular-nums;
  }

  .sess span {
    display: block;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-tertiary);
  }

  .sec {
    padding: 34px 0 26px;
    border-bottom: var(--line);
  }

  .h2 {
    padding-bottom: 4px;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 28px;
    letter-spacing: -0.01em;
    line-height: 1.15;
  }

  .list > * + * {
    border-top: var(--line);
  }

  /* My info: one row per field; a click anywhere on the row opens the edit. */
  .row {
    appearance: none;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    width: 100%;
    padding: 20px 0;
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr) auto;
    gap: 24px;
    align-items: center;
  }

  .k {
    font-size: 14.5px;
    color: var(--text-tertiary);
  }

  .v {
    font-size: 17px;
    font-weight: 500;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .unset {
    font-weight: 400;
    color: var(--text-muted);
  }

  .act {
    font-size: 14.5px;
    color: var(--text-tertiary);
  }

  /* Editing in place: the same rows, the value turned into its field. */
  .field {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 24px;
    align-items: start;
    padding: 14px 0;
  }

  .field .k {
    padding-top: 14px;
  }

  .on .k {
    color: var(--accent);
  }

  .input {
    max-width: 460px;
    height: 48px;
    border-radius: 8px;
    border: 1px solid var(--field-line);
    background: rgba(var(--fg-page-rgb), 0.03);
    display: flex;
    align-items: center;
    padding: 0 14px;
    font-size: 15.5px;
  }

  .input input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }

  .input input:read-only {
    color: var(--text-secondary);
  }

  .on .input {
    border-color: rgba(var(--accent-rgb), 0.6);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.12);
  }

  .input.bad,
  .on .input.bad {
    border-color: var(--danger);
    box-shadow: 0 0 0 3px rgba(var(--danger-rgb), 0.12);
  }

  .fieldErr {
    display: block;
    max-width: 460px;
    margin-top: 7px;
    font-size: 13.5px;
    line-height: 1.4;
    color: var(--danger);
  }

  .err {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14.5px;
    line-height: 1.45;
    color: var(--danger);
  }

  .editFoot {
    padding: 10px 0 4px 174px;
    display: grid;
    gap: 18px;
    justify-items: start;
  }

  .btns {
    display: flex;
    gap: 12px;
  }

  /* Payment methods: the saved cards, the brands as the app draws them. */
  .card {
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) auto;
    gap: 20px;
    align-items: center;
    padding: 18px 0;
  }

  .brand {
    width: 48px;
    height: 32px;
    border-radius: 6px;
    display: grid;
    place-items: center;
    font-family: var(--font-body);
    font-weight: 700;
    font-size: 11.5px;
    letter-spacing: 0.04em;
    position: relative;
  }

  /* The card networks' own colours, as the app's ProfilePage draws them (not theme colours). */
  .visa {
    background: linear-gradient(135deg, #1a2a6c 0%, #2557d6 100%);
    color: var(--text-on-accent);
    font-style: italic;
  }

  .mc {
    background: #201812;
    border: 1px solid var(--border-soft);
  }

  .mc::before,
  .mc::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 15px;
    height: 15px;
    border-radius: 50%;
  }

  .mc::before {
    left: 10px;
    background: #eb001b;
  }

  .mc::after {
    right: 10px;
    background: #f79e1b;
    mix-blend-mode: screen;
  }

  .other {
    background: var(--bg-card-2);
    color: var(--text-secondary);
  }

  .num {
    font-size: 17px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  .dots {
    letter-spacing: 0.16em;
  }

  .exp {
    margin-top: 3px;
    font-size: 14px;
    color: var(--text-tertiary);
    font-variant-numeric: tabular-nums;
  }

  .remove {
    appearance: none;
    border: 0;
    background: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 0;
    font: inherit;
    font-size: 14.5px;
    color: var(--text-tertiary);
  }

  .note {
    padding: 16px 0 0;
    font-size: 14px;
    color: var(--text-tertiary);
  }

  .empty {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 18px 0;
  }

  .orb {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: radial-gradient(circle at 30% 28%, rgba(var(--accent-hot-rgb), 0.24) 0%, rgba(var(--accent-deep-rgb), 0.1) 100%);
    box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.2);
    color: var(--accent);
  }

  .emptyT {
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 23px;
    line-height: 1.15;
  }

  .emptyS {
    margin-top: 4px;
    font-size: 15px;
    line-height: 1.5;
    color: var(--text-secondary);
  }

  .quiet {
    padding: 20px 0;
    font-size: 15px;
    color: var(--text-tertiary);
  }

  /* Preferences: the time zone, display only. */
  .set {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 24px;
    align-items: center;
    padding: 20px 0;
  }

  .by {
    font-weight: 400;
    color: var(--text-tertiary);
  }

  .foot {
    margin-top: 56px;
    text-align: center;
    display: grid;
    gap: 8px;
    justify-items: center;
  }

  .legal {
    display: flex;
    gap: 10px;
    font-size: 14.5px;
    color: var(--text-secondary);
  }

  .legal span {
    color: var(--text-muted);
  }

  .brandLine {
    font-size: 13.5px;
    color: var(--text-tertiary);
  }

  .brandLine b {
    font-weight: 600;
    color: var(--text-secondary);
  }

  .ver {
    font-size: 12px;
    color: var(--text-muted);
  }

  .fail {
    max-width: 720px;
    padding: 150px 0 40px;
    display: grid;
    justify-items: center;
    gap: 22px;
    text-align: center;
  }

  [data-look='light'] .on .k {
    color: var(--accent-hot);
  }

  @media (hover: hover) {
    .row:hover .act {
      color: var(--text-primary);
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    .remove:hover {
      color: var(--danger);
    }

    .legal a:hover {
      color: var(--text-primary);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  @media (max-width: 1023px) {
    .head {
      flex-direction: column;
      align-items: flex-start;
    }

    .row,
    .field,
    .set {
      grid-template-columns: 1fr auto;
    }

    .editFoot {
      padding-left: 0;
    }
  }

  /* web-studio-sites D12: a custom site's Profile (telify-pages.html .pfHead, .pfSess and .pfList): no kicker over
     her name, the session count's label in sentence case, a line between the rows. */
  :global(:root[data-custom-site]) .kicker {
    display: none;
  }

  :global(:root[data-custom-site]) .sess span {
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
  }

  :global(:root[data-custom-site]) .list > * + * {
    border-top: var(--line);
  }

  /* web-studio-sites D12: a custom site's narrow Profile (telify-pages.html .pfHead, .pfRow, .pfField and .pfSet at
     860 pixels): the name over the session count, the labels in a narrower column, a field under its label. */
  @media (max-width: 860px) {
    :global(:root[data-custom-site]) .head {
      gap: 22px;
      margin-top: 36px;
    }

    :global(:root[data-custom-site]) .who {
      gap: 18px;
    }

    :global(:root[data-custom-site]) .av {
      width: 64px;
      height: 64px;
      font-size: 21px;
    }

    :global(:root[data-custom-site]) .h1 {
      font-size: 38px;
    }

    :global(:root[data-custom-site]) .meta {
      margin-top: 6px;
      font-size: 15px;
    }

    :global(:root[data-custom-site]) .sess {
      display: flex;
      align-items: baseline;
      gap: 10px;
      padding: 0;
      text-align: left;
    }

    :global(:root[data-custom-site]) .sess b {
      font-size: 32px;
    }

    :global(:root[data-custom-site]) .sess span {
      margin: 0;
    }

    :global(:root[data-custom-site]) .row {
      grid-template-columns: 104px minmax(0, 1fr) auto;
      gap: 14px;
    }

    :global(:root[data-custom-site]) .field {
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
    }

    :global(:root[data-custom-site]) .field .k {
      padding-top: 0;
    }

    :global(:root[data-custom-site]) .btns > * {
      flex: 1;
    }

    :global(:root[data-custom-site]) .set {
      grid-template-columns: 104px minmax(0, 1fr);
      gap: 14px;
    }
  }
}
